import { useState, useEffect } from 'react';

import { CMS_API_URL } from '../config/apiBase';

/**
 * Fetch a public "collection shop" listing by its per-brand collection slug:
 * the collection's product items as full variation payloads (tier prices,
 * pinned colors) plus package cards. Backs the CollectionShop pages
 * (/clothing/{feSlug}/collection/:collectionSlug — rep-distributed price-tier
 * links with no school in the URL; school + class are collected at checkout).
 *
 * @param {string} brandSlug — brand API slug (e.g. 'abcse')
 * @param {string} collectionSlug — the collection's public slug
 * @returns {{ collection: {id,name,slug}|null, variations: Array,
 *             packages: Array, loading: boolean, error: Error|null }}
 */
export default function useCollectionListing(brandSlug, collectionSlug) {
  const [data, setData] = useState({ collection: null, variations: [], packages: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brandSlug || !collectionSlug) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${CMS_API_URL}/api/collections/${encodeURIComponent(brandSlug)}/${encodeURIComponent(collectionSlug)}/listing`,
          { signal: controller.signal }
        );

        if (!res.ok) throw new Error(`Collection listing API returned ${res.status}`);

        const json = await res.json();
        if (!cancelled) {
          setData({
            collection: json.collection ?? null,
            variations: Array.isArray(json.variations) ? json.variations : [],
            packages:   Array.isArray(json.packages) ? json.packages : [],
          });
        }
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          console.error(`[useCollectionListing] Failed for "${brandSlug}/${collectionSlug}":`, err);
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [brandSlug, collectionSlug]);

  return { ...data, loading, error };
}
